import { AuthShell, Button } from '@concord/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { afterSignIn } from '../../lib/session';
import s from './auth.module.css';
import { AUTH_IMAGES } from './auth-image';

interface Preview {
  firstName: string;
  role: 'patient' | 'navigator';
  state: 'valid' | 'expired' | 'used';
}

/**
 * Account invitation landing (IAM-03, IAM-07). Opening the account takes the 6-digit code we
 * email to the invited address, so the address is verified and a forwarded link alone opens
 * nothing. Staff then set up their authenticator app before anything else.
 */
export function Welcome() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api<Preview>(`/auth/invitations/${encodeURIComponent(token)}`),
    retry: false,
  });

  async function sendCode() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ sentTo: string }>('/auth/invitations/verify', { body: { token } });
      setSentTo(r.sentTo);
      setCode('');
    } catch (e) {
      setError(errorMessage(e));
    }
    setBusy(false);
  }

  async function accept(e?: FormEvent) {
    e?.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ realm: 'staff' | 'patient' }>('/auth/invitations/accept', {
        body: { token, code },
      });
      const to = await afterSignIn(qc, r.realm, r.realm === 'staff' ? '/console' : '/home');
      navigate(to, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const p = preview.data;
  return (
    <AuthShell image={AUTH_IMAGES[p?.role === 'navigator' ? 'staff' : 'patient']}>
      <div className={s.card}>
        {preview.isPending ? (
          <div className="skeleton" style={{ height: 120 }} />
        ) : p?.state !== 'valid' ? (
          <>
            <h1>This link has {p?.state === 'used' ? 'already been used' : 'expired'}</h1>
            <p className={s.sent}>
              {p?.state === 'used'
                ? 'Your account is already set up. Sign in with your email address.'
                : 'Ask your care navigator to send you a new link.'}
            </p>
            {p?.state === 'used' && (
              <Button
                size="lg"
                onClick={() => navigate(p.role === 'navigator' ? '/console/sign-in' : '/sign-in')}
              >
                Sign in
              </Button>
            )}
          </>
        ) : (
          <>
            <span className={s.realm}>
              {p.role === 'patient' ? 'Patient portal' : 'Care navigator console'}
            </span>
            <h1>Welcome, {p.firstName}</h1>
            <p className={s.lead}>
              {p.role === 'patient'
                ? 'Your account at Cancer Expert Now is ready. From it you send your case to a panel of specialist physicians, add your records and see where your case stands.'
                : 'Your staff account on the care navigator console is ready.'}
            </p>
            {sentTo ? (
              <form onSubmit={accept} className={s.codeForm}>
                <label htmlFor="welcome-code" className={s.fine}>
                  We emailed a 6-digit code to <b>{sentTo}</b>. Enter it to confirm your email address.
                </label>
                <input
                  id="welcome-code"
                  className="input code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                {error && <p className="notice bad">{error}</p>}
                <Button size="lg" type="submit" busy={busy} disabled={code.length !== 6}>
                  Confirm and open my account
                </Button>
                <button type="button" className={s.linkBtn} onClick={sendCode} disabled={busy}>
                  Send a new code
                </button>
              </form>
            ) : (
              <>
                <p className={s.fine}>
                  {p.role === 'navigator'
                    ? 'There is no password. First we email you a code to confirm your address; then you set up an authenticator app.'
                    : 'There is no password. First we email you a code to confirm your address. Each time you sign in, we email you a code.'}
                </p>
                {error && <p className="notice bad">{error}</p>}
                <Button size="lg" busy={busy} onClick={sendCode}>
                  Open my account
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </AuthShell>
  );
}
